import {
   GenericSearchResultItem,
   ProductSearchResultItem,
   NewsSearchResultItem,
   DeviceSearchResultItem,
   GuideSearchResultItem,
   SearchResultItem,
} from '../../types';
import { usePriceTier } from '@ifixit/app/hooks';
import { Highlight } from '../highlight';
import { useTranslations } from '@ifixit/i18n';
import { type LocaleCode } from '@ifixit/i18n';
import { Doctype } from '../../types/indices';
import { determineProductPrice } from '../../utils/price-helpers';

/**
 * Formats a timestamp into a localized date string
 */
function formatTimestampToDate(timestamp: number): string {
   if (!timestamp) return '';
   const date = new Date(timestamp * 1000);
   return date.toLocaleDateString();
}

/**
 * News result component
 */
function NewsResult({ hit }: { hit: NewsSearchResultItem }) {
   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch">
         <div className="self-stretch text-gray-900 font-medium text-sm">
            <Highlight attribute="post_title" hit={hit} />
         </div>
         {hit.post_date && (
            <div className="text-gray-600 text-xs">
               {formatTimestampToDate(hit.post_date as number)}
            </div>
         )}
         {hit.content && (
            <div className="text-gray-800 text-sm mt-1">
               <Highlight attribute="content" hit={hit} isSnippet={true} />
            </div>
         )}
      </div>
   );
}

/**
 * Determines if a guide should be marked as featured
 */
function isGuideFeatured(hit: GuideSearchResultItem): boolean {
   return !!(hit.flags && hit.flags.includes('GUIDE_STARRED'));
}

/**
 * Guide result component
 */
function GuideResult({ hit }: { hit: GuideSearchResultItem }) {
   const isFeatured = isGuideFeatured(hit);
   const t = useTranslations('Search.cards');

   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch">
         <div className="flex justify-between w-full items-start">
            <div className="text-gray-900 font-medium text-sm">
               <Highlight attribute="title" hit={hit} />
            </div>
            {isFeatured && (
               <div className="bg-brand-700 text-white text-xs font-bold uppercase px-2 py-0.5 rounded flex-shrink-0 self-start">
                  {t('featured')}
               </div>
            )}
         </div>
         {hit.content && (
            <div className="text-gray-800 text-sm mt-1">
               <Highlight attribute="content" hit={hit} isSnippet={true} />
            </div>
         )}
      </div>
   );
}

/**
 * Product result component showing price info
 */
function ProductResult({
   hit,
   currency,
   localeCode,
   storeCode,
}: {
   hit: ProductSearchResultItem;
   currency: string;
   localeCode: LocaleCode;
   storeCode: string;
}) {
   const { priceTier } = usePriceTier({ storeCode });
   const priceDisplay = determineProductPrice(hit, priceTier, currency, localeCode);

   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch">
         <div className="self-stretch text-gray-900 font-medium text-sm">
            <Highlight attribute="title" hit={hit} />
         </div>
         {priceDisplay && <div className="text-gray-800 text-sm">{priceDisplay}</div>}
      </div>
   );
}

/**
 * Device result component
 */
function DeviceResult({ hit }: { hit: DeviceSearchResultItem }) {
   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch text-gray-900 font-medium text-sm">
         <Highlight attribute="title" hit={hit} />
      </div>
   );
}

/**
 * Generic result content component
 */
function GenericResult({ hit }: { hit: GenericSearchResultItem }) {
   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch">
         <div className="self-stretch text-gray-900 font-medium text-sm">
            <Highlight attribute="title" hit={hit} />
         </div>
         {hit.description && (
            <div className="text-gray-800 text-sm">
               <Highlight attribute="description" hit={hit} isSnippet={true} />
            </div>
         )}
      </div>
   );
}

/**
 * Renders the appropriate result component based on doctype
 */
export function ResultContent({
   hit,
   currency,
   localeCode,
   storeCode,
}: {
   hit: SearchResultItem;
   currency: string;
   localeCode: LocaleCode;
   storeCode: string;
}) {
   switch (hit.doctype as Doctype) {
      case 'product': {
         return (
            <ProductResult
               hit={hit as ProductSearchResultItem}
               currency={currency}
               localeCode={localeCode}
               storeCode={storeCode}
            />
         );
      }
      case 'topic': {
         return <DeviceResult hit={hit as DeviceSearchResultItem} />;
      }
      case 'news': {
         return <NewsResult hit={hit as NewsSearchResultItem} />;
      }
      case 'guide': {
         return <GuideResult hit={hit as GuideSearchResultItem} />;
      }
      default: {
         return <GenericResult hit={hit as GenericSearchResultItem} />;
      }
   }
}
